// src/pages/ForPublishersPage.jsx
// Public page for publishers - AI/SEO discovery
import React from 'react';
import { Link } from 'react-router-dom';
import UniversalFooter from '../components/UniversalFooter';
import { Seo, PAGE_SEO } from '../components/Seo';

const ForPublishersPage = () => {
  return (
    <div className="min-h-screen bg-slate-950 text-slate-100">
      <Seo {...PAGE_SEO.forPublishers} />

      <main className="max-w-4xl mx-auto px-6 py-16">
        <article className="prose prose-invert prose-slate max-w-none">
          <h1 className="text-4xl font-bold text-white mb-6">NewAudia for Publishers</h1>

          <p className="text-lg text-slate-300 leading-relaxed">
            NewAudia is an audio-first publishing platform built to support interactive audiobooks and
            NFD-SmartBooks<sup className="text-[0.6em]">™</sup>. It is designed for publishers seeking modern distribution models beyond
            traditional audiobook marketplaces.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">A Platform Built for Utility</h2>
          <p className="text-slate-300">
            Many audiobooks are consumed passively, limiting their usefulness for learning,
            reference, and long-term value. NewAudia focuses on structured audio experiences that
            improve retention, revisitability, and reader satisfaction.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">What Publishers Gain</h2>
          <ul className="space-y-3 text-slate-300">
            <li>Support for interactive audiobook formats</li>
            <li>NFD-SmartBook<sup className="text-[0.6em]">™</sup> publishing models designed for structured content</li>
            <li>Access to readers seeking deeper engagement with audio content</li>
            <li>Alternative distribution and monetization frameworks</li>
          </ul>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Designed for Modern Catalogs</h2>
          <p className="text-slate-300">
            NewAudia is particularly well-suited for nonfiction, educational, professional,
            and reference-oriented catalogs—where structure and accessibility are critical
            to reader outcomes.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Audience Alignment</h2>
          <p className="text-slate-300">
            The NewAudia audience values clarity, intent-driven consumption, and interaction.
            This creates alignment between publishers, authors, and readers who expect more
            from audio than background listening.
          </p>

          <h2 className="text-2xl font-semibold text-white mt-10 mb-4">Publishing with NewAudia</h2>
          <p className="text-slate-300">
            Publishers can explore NewAudia as a complementary distribution channel,
            particularly for titles where traditional audiobook formats fail to preserve
            the original value of the work.
          </p>

          <p className="text-slate-300 mt-6">
            Learn more at <a href="https://www.newaudia.com/" className="text-indigo-400 hover:text-indigo-300 underline">https://www.newaudia.com/</a>
          </p>

          <div className="mt-10 pt-6 border-t border-slate-800">
            <p className="text-slate-400 text-sm">Related pages:</p>
            <ul className="flex flex-wrap gap-4 mt-2">
              <li><Link to="/how-it-works" className="text-indigo-400 hover:text-indigo-300 underline text-sm">How It Works</Link></li>
              <li><Link to="/why-newaudia" className="text-indigo-400 hover:text-indigo-300 underline text-sm">Why NewAudia</Link></li>
              <li><Link to="/for-authors" className="text-indigo-400 hover:text-indigo-300 underline text-sm">For Authors</Link></li>
            </ul>
          </div>

          <p className="text-sm text-slate-500 mt-12 italic">Last updated: 2026-01-08</p>
        </article>
      </main>

      <UniversalFooter />
    </div>
  );
};

export default ForPublishersPage;
